import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, Grid, IconButton, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DataTable, { TableColumn } from "react-data-table-component";
import { MdAdd } from "react-icons/md";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { history } from "../..";
import contractorCompanyService from "../../services/contractorCompanyService";
import { SetContractorCompaniesAction } from "../../interface/redux-types/SetContractorCompaniesAction";
import { ContractorCompany } from "../../interface/api/ContractorCompany";
import {
  clearContractorCompany,
  clearContractorCompanyContacts,
  getContractorCompanies,
  setContractorCompanies,
} from "../../store/contractorStore";
import PageHero from "../shared/page-hero";
import DeleteDialog from "../shared/dialog/delete";
import { tableCustomStyles } from "../../utils/table-styles";
import useTimeout from "../../hooks/useTimeout";
import Button from "../shared/Button";
import {
  ActionIconGroup,
  DeleteIcon,
  EditIcon,
  OverThresholdIcon,
  StatusIconGroup,
  MainPill,
  SmallQuantityPill,
  HydrovacPill,
  RubblePill,
} from "../shared/icons";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";

const useStyles = makeStyles({
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const ContractorsView = () => {
  const classes = useStyles();

  const contractorCompanies = useAppSelector(getContractorCompanies);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Partial<ContractorCompany>>(
    {}
  );
  const [
    contractorIdsThatAreOverThreshold,
    setContractorIdsThatAreOverThreshold,
  ] = useState<number[]>([]);
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  const [name, setName] = useState("");

  useEffect(() => {
    return () => {
      dispatch(clearContractorCompanyContacts());
    };
  }, []);

  useTimeout(() => dispatch(clearContractorCompany()), 100);

  useMemo(() => {
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok) {
        const contractorCompaniesWithThresholdsResponse =
          response.data as ContractorCompaniesWithThresholdsResponse;
        const contractorCompanies =
          contractorCompaniesWithThresholdsResponse.contractorCompanies as ContractorCompany[];

        setContractorIdsThatAreOverThreshold(
          contractorCompaniesWithThresholdsResponse.contractorIdsThatAreOverThreshold
        );

        const setContractorCompaniesAction: SetContractorCompaniesAction = {
          contractorCompanies: contractorCompanies,
        };
        dispatch(setContractorCompanies(setContractorCompaniesAction));
      }
    });
  }, []);

  const filterContractorCompanies = useMemo(() => {
    if (!name) {
      return contractorCompanies;
    } else {
      return contractorCompanies.filter((c) =>
        c.name.toLowerCase().includes(name.toLowerCase())
      );
    }
  }, [contractorCompanies, name]);

  const onConfirmDelete = () => {
    if (selectedItem && selectedItem.id) {
      contractorCompanyService
        .deleteContractorCompany(selectedItem.id)
        .then((response) => {
          if (response.ok) {
            const setContractorCompaniesAction: SetContractorCompaniesAction = {
              contractorCompanies: contractorCompanies.filter(
                (c) => c.id !== selectedItem.id
              ),
            };
            dispatch(setContractorCompanies(setContractorCompaniesAction));
          }
        });
    }

    onClose();
  };

  const columns: TableColumn<ContractorCompany>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Type",
      cell: (row) => (
        <>
          {row.isMainTicket && <MainPill />}
          {row.isSmallQuantity && <SmallQuantityPill />}
          {row.isHydrovac && <HydrovacPill />}
          {row.isRubble && <RubblePill />}
        </>
      ),
    },
    {
      name: "Status",
      cell: (row) => (
        <StatusIconGroup>
          {contractorIdsThatAreOverThreshold.includes(row.id) && (
            <OverThresholdIcon />
          )}
        </StatusIconGroup>
      ),
    },
    {
      name: "Actions",
      cell: (row) =>
        !row.isMiscellaneous ? (
          <ActionIconGroup spread>
            <EditIcon
              onClick={() =>
                history.push(`/contractor-company/${row.id}`)
              }
            />
            <DeleteIcon
              onClick={() => {
                setSelectedItem(row);
                onOpen();
              }}
            />
          </ActionIconGroup>
        ) : (
          <></>
        ),
    },
  ];

  return (
    <>
      <Container maxWidth="xl">
        <PageHero
          title="Contractors"
          subtitle="Add and manage Contractor Companies."
          showBackButton={false}
          action={
            <Tooltip title="Add Contractor Company">
              <IconButton
                color="primary"
                className={classes.addIconButton}
                onClick={() => history.push(`/contractor-company/new`)}
              >
                <MdAdd size={24} />
              </IconButton>
            </Tooltip>
          }
        />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              type="text"
              variant="outlined"
              fullWidth
              label="Search by Name"
              name="name"
              onChange={(e) => setName(e.target.value)}
              value={name}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              type="button"
              onClick={() => setName("")}
              variant="contained"
              color="default"
              label="Clear"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs>
            <PaginatedTableTop>
              <DataTable
                columns={columns}
                data={filterContractorCompanies ?? []}
                customStyles={tableCustomStyles}
                className={paginationTopClassName}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Grid>
        </Grid>
      </Container>
      <DeleteDialog
        open={open}
        onClose={onClose}
        onConfirmDelete={onConfirmDelete}
      />
    </>
  );
};

export default connect()(ContractorsView);
